import { Box, Button, Flex, Loader, Table, Text } from "@mantine/core"
import { IconRefresh } from "@tabler/icons-react";
import { LEAGUE } from "../../models/NBA";
import { useCallback, useEffect, useState } from "react";
import { Season, TeamStanding } from "../../client";
import { getStandings } from "../../api/Info";

interface LeagueStandingsProps {
  league: LEAGUE
  conference?: string
  season: Season
}

const StandingsTable: React.FC<{ teams: TeamStanding[] }> = ({ teams }) => (
  <Box style={{ overflowX: 'auto' }}>
    <Table highlightOnHover fz={{ base: 'xs', sm: 'sm' }}>
      <Table.Thead>
        <Table.Tr>
          <Table.Th w={30} px={{ base: 4, sm: 8 }}></Table.Th>
          <Table.Th px={{ base: 4, sm: 8 }}>Team</Table.Th>
          <Table.Th ta="center" px={{ base: 4, sm: 8 }}>W-L</Table.Th>
          <Table.Th ta="center" px={{ base: 4, sm: 8 }}>PCT</Table.Th>
          <Table.Th ta="center" px={{ base: 4, sm: 8 }}>L10</Table.Th>
          <Table.Th ta="center" px={{ base: 4, sm: 8 }}>Trend</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {teams.map((team, i) => (
          <Table.Tr key={team.name}>
            <Table.Td px={{ base: 4, sm: 8 }}>
              <Text fz={{ base: 'xs', sm: 'sm' }} fw={500}>{i + 1}</Text>
            </Table.Td>
            <Table.Td px={{ base: 4, sm: 8 }}>
              <Text fz={{ base: 'xs', sm: 'sm' }} fw={500}>{team.name}</Text>
            </Table.Td>
            <Table.Td ta="center" px={{ base: 4, sm: 8 }}>{team.record}</Table.Td>
            <Table.Td ta="center" px={{ base: 4, sm: 8 }}>{team.winPct}</Table.Td>
            <Table.Td ta="center" px={{ base: 4, sm: 8 }}>{team.l10 ?? "-"}</Table.Td>
            <Table.Td
              ta="center"
              px={{ base: 4, sm: 8 }}
              c={team.currentStreak != null ? (team.currentStreak > 0 ? "green" : "red") : "dimmed"}
            >
              {team.currentStreak != null ? `${team.currentStreak > 0 ? "W" : "L"} ${Math.abs(team.currentStreak)}` : "-"}
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  </Box>
)

const LeagueStandings: React.FC<LeagueStandingsProps> = ({ league, conference, season }) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(false)
  const [standings, setStandings] = useState<TeamStanding[]>([])

  const startYear = parseInt(season.slice(0, 4), 10)
  const isPre1970 = !isNaN(startYear) && startYear < 1970

  const getGroupTitle = (group: string) => {
    const term = isPre1970 ? "Division" : "Conference"
    if (group === "East") return `Eastern ${term}`
    if (group === "West") return `Western ${term}`
    if (group === "Central") return `Central ${term}`
    return `${group} ${term}`
  }

  const fetchData = useCallback(async (signal?: AbortSignal) => {
    setLoading(true)
    setError(false)
    try {
      const standingsData = await getStandings(league, season, signal)
      if (!signal?.aborted) {
        setStandings(standingsData || [])
      }
    } catch (err: unknown) {
      if ((err as Error)?.name === "AbortError" || signal?.aborted) return
      setError(true)
    } finally {
      if (!signal?.aborted) {
        setLoading(false)
      }
    }
  }, [league, season])

  useEffect(() => {
    const controller = new AbortController()
    fetchData(controller.signal)
    return () => controller.abort()
  }, [fetchData])

  if (loading) {
    return (
      <Box p="md" bg={"#1f1f1f"} style={{ borderRadius: 16 }}>
        <Flex justify="center" align="center" mih={300}>
          <Loader size={50} />
        </Flex>
      </Box>
    )
  }

  if (error) {
    return (
      <Box p="md" bg={"#1f1f1f"} style={{ borderRadius: 16 }}>
        <Flex direction="column" align="center" justify="center" mih={300} gap="xs">
          <Text c="dimmed" fz="sm">Unable to load standings.</Text>
          <Button
            size="xs"
            variant="subtle"
            color="gray"
            radius="xl"
            onClick={() => fetchData()}
            leftSection={<IconRefresh size={14} />}
          >
            Retry
          </Button>
        </Flex>
      </Box>
    )
  }

  if (standings.length === 0) {
    return (
      <Box p="md" bg={"#1f1f1f"} style={{ borderRadius: 16 }}>
        <Flex direction="column" align="center" justify="center" mih={200}>
          <Text c="dimmed" fz="sm">No standings data available for {season}.</Text>
        </Flex>
      </Box>
    )
  }

  // Determine dynamic groups
  let groups: string[] = []
  if (conference) {
    groups = [conference]
  } else if (league === LEAGUE.NBA) {
    const rawGroups = Array.from(new Set(standings.map((t) => t.conference).filter((c): c is string => Boolean(c))))
    const groupOrder = ["East", "Central", "West"]
    groups = rawGroups.sort((a, b) => {
      const idxA = groupOrder.indexOf(a)
      const idxB = groupOrder.indexOf(b)
      if (idxA !== -1 && idxB !== -1) return idxA - idxB
      if (idxA !== -1) return -1
      if (idxB !== -1) return 1
      return a.localeCompare(b)
    })
  }

  if (groups.length === 0) {
    const unifiedTeams = [...standings].sort((a, b) => b.winPct - a.winPct || b.wins - a.wins)
    return (
      <Box p="md" bg={"#1f1f1f"} style={{ borderRadius: 16 }}>
        <Text fz="h4" fw={700} mb="md" style={{ letterSpacing: '0.4px' }}>League Standings</Text>
        <StandingsTable teams={unifiedTeams} />
      </Box>
    )
  }

  return (
    <Flex direction="row" wrap="wrap" gap="md" w="100%">
      {groups.map((group) => {
        const teams = standings
          .filter((t) => t.conference === group)
          .sort((a, b) => b.winPct - a.winPct || b.wins - a.wins)
        return (
          <Box
            key={group}
            flex="1 1 auto"
            miw={{ base: "100%", md: groups.length === 2 ? "48%" : "30%" }}
            p="md"
            bg={"#1f1f1f"}
            style={{ borderRadius: 16 }}
          >
            <Text fz="h4" fw={700} mb="md" style={{ letterSpacing: '0.4px' }}>
              {getGroupTitle(group)}
            </Text>
            <StandingsTable teams={teams} />
          </Box>
        )
      })}
    </Flex>
  )
}

export default LeagueStandings